import { useRef } from 'react';
import { ModalConfirm } from '@direct-frontend/components';
import { type Location, useHistoryBlock } from '@direct-frontend/modules-router';
import { renderTemporaryComponent } from '../../../dom';
import { UNSAVED_CHANGES_CONFIRM } from '../../unsavedChangesConfirm';

const {
    title: DEFAULT_HEADER_TEXT,
    bodyText: DEFAULT_BODY_TEXT,
    applyText: DEFAULT_BUTTON_SUCCESS_TEXT,
    cancelText: DEFAULT_BUTTON_CANCEL_TEXT,
    imageSrc: DEFAULT_IMAGE_SRC,
} = UNSAVED_CHANGES_CONFIRM;

export type FormConfirmHistoryTransitionOptions = {
    getDirty: (location?: Location) => boolean;
    headerText?: string;
    bodyText?: string;
    buttonSuccessText?: string;
    buttonCancelText?: string;
    onCancel?: (unblock?: () => void) => void;
    onSave?: () => void;
    onClose?: () => void;
    imageSrc?: string;
};

/**
 * Хук для подтверждения перехода по истории браузера с несохраненными данными
 *
 * @kind helper
 */
export function useFormConfirmHistoryTransition({
    getDirty,
    headerText = DEFAULT_HEADER_TEXT,
    bodyText = DEFAULT_BODY_TEXT,
    buttonCancelText = DEFAULT_BUTTON_CANCEL_TEXT,
    buttonSuccessText = DEFAULT_BUTTON_SUCCESS_TEXT,
    onSave,
    onCancel,
    onClose,
    imageSrc = DEFAULT_IMAGE_SRC,
}: FormConfirmHistoryTransitionOptions) {
    const refModalMount = useRef(false);
    const refIsDirty = useRef(getDirty);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    refIsDirty.current = getDirty;

    useHistoryBlock({
        isEnabled: getDirty(),
        block({ unblock, location }) {
            if (refModalMount.current) {
                return;
            }

            if (!refIsDirty.current(location)) {
                unblock();

                return;
            }

            const unmount = renderTemporaryComponent((unmount) => {
                refModalMount.current = true;

                return (
                    <ModalConfirm
                        isOpen
                        onClose={() => {
                            refModalMount.current = false;
                            onClose?.();
                            unmount();
                        }}
                        imageSrc={imageSrc}
                        headerText={headerText}
                        bodyText={bodyText}
                        preventScroll={false}
                        buttons={[
                            {
                                color: 'accent',
                                text: buttonSuccessText,
                                onClick: () => {
                                    refModalMount.current = false;
                                    onSave?.();
                                    unmount();
                                    unblock();
                                },
                            },
                            {
                                color: 'normal',
                                text: buttonCancelText,
                                onClick: () => {
                                    refModalMount.current = false;
                                    onCancel?.(unblock);
                                    unmount();
                                },
                            },
                        ]}
                    />
                );
            });

            // Очистит DOM при отмене блокировки
            return () => {
                refModalMount.current = false;
                unmount();
            };
        },
    });
}
